/*
 * Kraslaag over de teaser in het mega-menu.
 *
 * De laag is een canvas dat over de kaart ligt. Slepen wist er stukjes uit; is
 * er genoeg weg, dan verdwijnt de rest vanzelf.
 *
 * Wat dit lastiger maakt dan een kraslaag op een gewone pagina: een mega-menu
 * gaat dicht zodra de muis het paneel verlaat. Krassen vraagt juist dat je
 * blijft slepen. Drie dingen vangen dat op:
 *
 * 1. Zodra er gekrast wordt, houden we het <details> open. Los je de muisknop,
 *    dan geven we die grip weer terug.
 * 2. De voortgang gaat naar localStorage. Klapt het menu toch dicht, dan pak je
 *    het op waar je was in plaats van opnieuw te beginnen.
 * 3. Eenmaal onthuld blijft onthuld. Een verrassing die zichzelf opnieuw
 *    verstopt is geen verrassing meer maar een taak.
 *
 * De knop eronder doet hetzelfde in één klik. Die is er niet als vangnet voor
 * de kras maar omdat slepen niet voor iedereen werkt: met tab navigeren en
 * slepen gaat niet samen.
 *
 * Bij prefers-reduced-motion staat de kaart meteen open.
 */

(() => {
  const KWAST = 26;
  const DREMPEL = 55; // procent weggekrast voordat de rest vanzelf verdwijnt

  const minderBeweging = window.matchMedia('(prefers-reduced-motion: reduce)');

  const koppel = (kaart) => {
    if (kaart.dataset.krasGekoppeld === '1') return;
    kaart.dataset.krasGekoppeld = '1';

    const canvas = kaart.querySelector('[data-kras-laag]');
    const knop = kaart.querySelector('[data-kras-knop]');
    const hint = kaart.querySelector('[data-kras-hint]');
    const sleutel = 'collett-kras-' + (kaart.dataset.krasSleutel || 'teaser');
    if (!canvas) return;

    const onthul = (bewaren) => {
      kaart.classList.add('krasteaser--open');
      if (bewaren !== false) {
        try {
          localStorage.setItem(sleutel, 'open');
        } catch (e) {
          // Privémodus kan localStorage weigeren. De kaart is dan gewoon
          // onthuld voor deze sessie; dat is beter dan een foutmelding.
        }
      }
    };

    knop?.addEventListener('click', () => onthul());

    let alOpen = false;
    try {
      alOpen = localStorage.getItem(sleutel) === 'open';
    } catch (e) {
      alOpen = false;
    }

    if (alOpen || minderBeweging.matches) {
      onthul(false);
      return;
    }

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    let getekend = false;

    const vulLaag = () => {
      // De maat van het canvas zelf, niet van de wikkel eromheen. Die wikkel is
      // hoger -- er staat een kopje boven en een knop onder -- en dan tekende
      // de laag op de verkeerde afmeting.
      const r = canvas.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return false;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const groen = getComputedStyle(document.documentElement)
        .getPropertyValue('--collett-groen')
        .trim();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = groen ? `rgb(${groen})` : '#0e2a1c';
      ctx.fillRect(0, 0, r.width, r.height);

      getekend = true;
      return true;
    };

    const percentageWeg = () => {
      const beeld = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let leeg = 0;
      // Elke 16e pixel volstaat; alles aflopen is hier verspilde rekentijd.
      for (let i = 3; i < beeld.length; i += 4 * 16) {
        if (beeld[i] === 0) leeg++;
      }
      return (leeg / (beeld.length / (4 * 16))) * 100;
    };

    let bezig = false;

    const krasOp = (e) => {
      if (!getekend && !vulLaag()) return;

      const r = canvas.getBoundingClientRect();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(e.clientX - r.left, e.clientY - r.top, KWAST, 0, Math.PI * 2);
      ctx.fill();

      hint?.setAttribute('hidden', '');
    };

    /*
     * Het menu openhouden zolang er gekrast wordt.
     *
     * Het paneel sluit zichzelf zodra de muis eruit loopt, en tijdens het
     * slepen schiet je daar zo overheen. Een vlag zetten is niet genoeg -- er
     * moet iets zijn dat hem ook echt terugzet. Vandaar deze luisteraar: gaat
     * het menu dicht terwijl er gekrast wordt, dan gaat het meteen weer open.
     *
     * Zodra de muisknop los is verdwijnt de vlag en werkt het menu weer
     * normaal. Een menu dat blijft plakken is erger dan een kras die opnieuw
     * moet.
     */
    const details = kaart.closest('details');

    details?.addEventListener('toggle', () => {
      if (!details.open && details.dataset.krasBezig === '1') {
        details.open = true;
      }
    });

    canvas.addEventListener('pointerdown', (e) => {
      bezig = true;
      canvas.setPointerCapture(e.pointerId);
      if (details) details.dataset.krasBezig = '1';
      krasOp(e);
    });

    canvas.addEventListener('pointermove', (e) => {
      if (bezig) krasOp(e);
    });

    const stop = () => {
      if (!bezig) return;
      bezig = false;
      if (details) delete details.dataset.krasBezig;

      // Alleen meten als er ook echt een laag ligt. Een leeg canvas bestaat
      // volledig uit doorzichtige pixels en telt dus als honderd procent
      // weggekrast -- dan zou de verrassing weg zijn voordat iemand iets deed.
      if (!getekend) return;

      if (percentageWeg() >= DREMPEL) onthul();
    };

    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);
    canvas.addEventListener('pointerleave', stop);

    /*
     * Meteen vullen, en het nog een paar keer proberen.
     *
     * Het mega-menu verbergt zijn paneel niet met display: none -- de kaart
     * heeft zijn volle 238 bij 178 ook als het menu dicht is. Daardoor gaat er
     * later niets meer veranderen, en een ResizeObserver of het toggle-event
     * van <details> vuurt dus nooit. Beide geprobeerd; allebei bleef het canvas
     * op zijn standaard 300x150 staan.
     *
     * De herhalingen vangen het geval waarin de stylesheet nog niet binnen is
     * op het moment dat dit script draait. Zodra de laag er staat stoppen ze.
     */
    const probeer = (resterend) => {
      if (getekend || kaart.classList.contains('krasteaser--open')) return;
      if (vulLaag()) return;
      if (resterend > 0) setTimeout(() => probeer(resterend - 1), 250);
    };

    probeer(8);

    window.addEventListener('resize', () => {
      getekend = false;
      probeer(4);
    });
  };

  const init = () => document.querySelectorAll('[data-kras]').forEach(koppel);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
